import {
  Button,
  Checkbox,
  Collapse,
  Icon,
  Radio,
  RadioGroup,
} from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { ItemSelector, Text, Tooltip } from 'vet-bones/components';
import { SelectItem } from 'vet-bones/utils/select';

import { InlineEditableLabel } from 'src/components/InlineEditableLabel';
import {
  TEST_IDS,
  getPublishProjectDrawerCreationTypeTestId,
  getPublishProjectDrawerShouldPublishCheckboxTestId,
  getVirtualGraphItemTestId,
} from 'src/constants/testIds';
import {
  Mapping,
  MappingIdentifierType,
  RelationshipMapping,
} from 'src/containers/ProjectDiagram/types/data';
import {
  useFindVirtualGraph,
  useValidateVirtualGraphName,
  useVirtualGraphNamePlaceholder,
} from 'src/containers/PublishProjectDrawer/hooks';
import {
  CreationType,
  VirtualGraphMappingInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { connectionSelector } from 'src/selectors/connection';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { projectSelector } from 'src/selectors/project';
import { virtualGraphItemsSelector } from 'src/selectors/virtualGraphItems';
import { ProjectVirtualGraph } from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { useDesignerPermissions } from 'src/utils/hooks/useDesignerPermissions';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export type VirtualGraphMappingRowProps = {
  databaseId: string;
  info: VirtualGraphMappingInfo;
  initialInfo: VirtualGraphMappingInfo;
  mappings: Mapping[];
  relationshipMappings: RelationshipMapping[];
  setInfo: (info: VirtualGraphMappingInfo) => void;
  testId?: string;
  virtualGraph: ProjectVirtualGraph;
};

export const VirtualGraphMappingRow = ({
  databaseId,
  info,
  initialInfo,
  mappings,
  relationshipMappings,
  setInfo,
  testId,
  virtualGraph,
}: VirtualGraphMappingRowProps) => {
  const { projectId } = useRouterParams();
  const { canPublishVirtualGraphs } = useDesignerPermissions();
  const project = useReduxSelector(projectSelector(projectId));

  const didVirtualGraphChange = useDidValueChange(virtualGraph);
  const findVirtualGraph = useFindVirtualGraph();
  const validate = useValidateVirtualGraphName();
  const virtualGraphNamePlaceholder = useVirtualGraphNamePlaceholder(
    databaseId,
    virtualGraph
  );
  const virtualGraphSelectItems = useReduxSelector(
    virtualGraphItemsSelector(databaseId)
  );
  const connection = useReduxSelector(connectionSelector);
  const isCatalogEnabled = useReduxSelector(
    isStardogFeatureSupportedSelector('isCatalogSupported', connection)
  );
  const isStaticIriRelationshipMappingSupported = useReduxSelector(
    isStardogFeatureSupportedSelector(
      'isStaticIriRelationshipMappingSupported',
      connection
    )
  );

  const [isOpen, setIsOpen] = React.useState(false);

  const initialExistingVirtualGraph =
    initialInfo.creationType === CreationType.NEW
      ? null
      : findVirtualGraph(info.virtualGraphName) || null;
  const [existing, setExisting] = React.useState<SelectItem | null>(
    initialExistingVirtualGraph
  );

  React.useEffect(() => {
    if (didVirtualGraphChange) {
      setExisting(initialExistingVirtualGraph);
      const expand = Boolean(
        initialInfo.creationType === CreationType.NEW &&
          validate(initialInfo.virtualGraphName)
      );

      if (expand) {
        setIsOpen(true);
      }
    }
  }, [
    didVirtualGraphChange,
    initialExistingVirtualGraph,
    initialInfo,
    project,
    validate,
    virtualGraph,
  ]);

  const handleSetInfo = React.useCallback(
    (nextInfo: Partial<VirtualGraphMappingInfo>) => {
      setInfo({ ...info, ...nextInfo });
    },
    [info, setInfo]
  );

  const { creationType, virtualGraphName } = info;

  const handleSelectExisting = React.useCallback(
    (existing: SelectItem | null) => {
      setExisting(existing);
      if (existing?.value) {
        handleSetInfo({ virtualGraphName: existing.value });
      }
    },
    [handleSetInfo]
  );

  const handleChangeCreationType = React.useCallback(
    ({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) => {
      const newCreationType = value as CreationType;
      if (newCreationType === CreationType.NEW) {
        handleSetInfo({
          creationType: newCreationType,
          virtualGraphName: virtualGraphNamePlaceholder,
        });
        if (!virtualGraphNamePlaceholder) {
          setIsOpen(true);
        }
      } else if (newCreationType === CreationType.REPLACE) {
        handleSetInfo({
          creationType: newCreationType,
          virtualGraphName: existing?.value || '',
        });
      }
    },
    [existing, handleSetInfo, virtualGraphNamePlaceholder]
  );

  const errors: string[] = [];
  if (
    !isCatalogEnabled &&
    typeof virtualGraph.from !== 'string' &&
    Boolean(virtualGraph.from.catalog)
  ) {
    errors.push(
      copy.components.publishProjectDrawer.mappingConfiguration.virtualGraph
        .versionWarning
    );
  }

  if (
    !isStaticIriRelationshipMappingSupported &&
    relationshipMappings.some(
      (mapping) =>
        (mapping.fromPrimaryIdentifier &&
          !mapping.fromPrimaryIdentifier.parts.some(
            (part) => part.type === MappingIdentifierType.FIELD
          )) ||
        (mapping.toPrimaryIdentifier &&
          !mapping.toPrimaryIdentifier.parts.some(
            (part) => part.type === MappingIdentifierType.FIELD
          ))
    )
  ) {
    errors.push(
      copy.components.publishProjectDrawer.mappingConfiguration.virtualGraph
        .staticIriWarning
    );
  }

  const disabled = !info.shouldPublish;

  return (
    <div className="sd-resource-mapping-row" data-testid={testId}>
      <div className="sd-resource-mapping-row__body">
        <div className="sd-resource-mapping-grid">
          <div className="sd-resource-mapping-grid__should-publish">
            <Tooltip
              content={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .virtualGraph.insufficientPrivilegesVirtualGraphs
              }
              disabled={canPublishVirtualGraphs}
            >
              <Checkbox
                checked={info.shouldPublish}
                data-testid={getPublishProjectDrawerShouldPublishCheckboxTestId(
                  virtualGraph.id
                )}
                disabled={!canPublishVirtualGraphs}
                label={undefined}
                onChange={({ currentTarget: { checked } }) => {
                  handleSetInfo({ shouldPublish: checked });
                  if (!checked) {
                    setIsOpen(false);
                  }
                }}
              />
            </Tooltip>
          </div>
          <div className="sd-resource-mapping-grid__resource-name">
            <Text ellipsize>{virtualGraph.name}</Text>
            {errors.length ? (
              <Tooltip content={errors.join('\n\n')}>
                <Icon icon={IconNames.WARNING_SIGN} />
              </Tooltip>
            ) : null}
          </div>
          <div className="sd-resource-mapping-row__mappings">
            {mappings.map(({ key, text }) => (
              <Text key={key} ellipsize>
                {text}
              </Text>
            ))}
          </div>
          <RadioGroup
            disabled={disabled}
            inline
            onChange={handleChangeCreationType}
            selectedValue={creationType}
          >
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.NEW
              )}
              disabled={disabled}
              label={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .virtualGraph.create
              }
              value={CreationType.NEW}
            />
            <Radio
              data-testid={getPublishProjectDrawerCreationTypeTestId(
                CreationType.REPLACE
              )}
              disabled={disabled}
              labelElement={
                copy.components.publishProjectDrawer.mappingConfiguration
                  .virtualGraph.replace
              }
              value={CreationType.REPLACE}
            />
            <div className="sd-drawer-selector-menu">
              <ItemSelector.Single
                buttonText={existing?.text || ''}
                disabled={creationType !== CreationType.REPLACE || disabled}
                filterText={copy.components.defaults.filter}
                items={virtualGraphSelectItems}
                itemTestId={getVirtualGraphItemTestId}
                onSelect={handleSelectExisting}
                selected={existing}
                testId={TEST_IDS.virtualGraph.selector}
              />
            </div>
          </RadioGroup>

          <div className="sd-resource-mapping-row__collapse-button">
            <Button
              data-testid={TEST_IDS.publishProjectDrawer.mapping.collapseButton}
              disabled={disabled}
              icon={isOpen ? IconNames.CHEVRON_UP : IconNames.CHEVRON_DOWN}
              minimal
              onClick={() => setIsOpen(!isOpen)}
            />
          </div>
        </div>
      </div>

      <Collapse isOpen={isOpen}>
        <div
          className="sd-resource-mapping-row__collapse-body"
          data-testid={TEST_IDS.publishProjectDrawer.mapping.collapseBody}
        >
          <InlineEditableLabel
            disabled={creationType === CreationType.REPLACE || disabled}
            label={
              copy.components.publishProjectDrawer.mappingConfiguration
                .virtualGraph.label
            }
            onSetValue={(nextVirtualGraphName) =>
              handleSetInfo({ virtualGraphName: nextVirtualGraphName })
            }
            onValidate={
              creationType === CreationType.REPLACE ? () => '' : validate
            }
            placeholder={virtualGraphNamePlaceholder}
            testId={TEST_IDS.publishProjectDrawer.mapping.vgNameInput}
            value={
              creationType === CreationType.REPLACE && !existing
                ? copy.components.publishProjectDrawer.mappingConfiguration
                    .virtualGraph.placeholder
                : virtualGraphName
            }
          />
        </div>
      </Collapse>
    </div>
  );
};
